import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useAuthStore } from "@/features/auth/auth-store";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

import { StageProgress } from "./StageProgress";
import { StageIntro } from "./StageIntro";
import { StageTheory } from "./StageTheory";
import { StageSimulation } from "./StageSimulation";
import { StageSummary } from "./StageSummary";
import { StageHook } from "./StageHook";
import { StageConcept } from "./StageConcept";
import { StageDecision } from "./StageDecision";
import { StageReveal } from "./StageReveal";
import { StageTakeaway } from "./StageTakeaway";
import { StageQuiz } from "./StageQuiz";
import { LessonComplete } from "./LessonComplete";
import { getNextLesson } from "../content";
import { startLesson, updateStage, completeLesson, addXp, seedReviews } from "../api";
import { markStarted, markStage, markCompleted } from "../progress-store";
import { useXpStore } from "../xp-store";
import { lt } from "../localize";
import { trackEvent } from "@/lib/analytics";
import type { Lesson, StageType } from "../types";

const STAGE_TYPE_ICONS: Record<StageType, string> = {
  hook: "\uD83C\uDFAF",
  concept: "\uD83D\uDCA1",
  simulation: "\uD83C\uDCCF",
  simulation_adjust: "\uD83C\uDCCF",
  decision: "\u2660\uFE0F",
  reveal: "\u2728",
  takeaway: "\u2B50",
  quiz: "\uD83E\uDDE0",
  intro: "\uD83D\uDCD6",
  theory: "\uD83D\uDCD6",
  summary: "\uD83D\uDCD6",
};

interface LessonViewerProps {
  lesson: Lesson;
}

export const LessonViewer = ({ lesson }: LessonViewerProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const token = useAuthStore((s) => s.token);
  const setXp = useXpStore((s) => s.setXp);
  const addBadges = useXpStore((s) => s.addBadges);

  const [currentStage, setCurrentStage] = useState(0);
  const [earnedXp, setEarnedXp] = useState(0);
  const [newBadges, setNewBadges] = useState<string[]>([]);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [quizCorrect, setQuizCorrect] = useState(false);
  const [quizSelectedIndex, setQuizSelectedIndex] = useState<number>(-1);
  const [completed, setCompleted] = useState(false);
  const [started, setStarted] = useState(false);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const [stageKey, setStageKey] = useState(0);

  const stage = lesson.stages[currentStage];
  const isLastStage = currentStage === lesson.stages.length - 1;
  const nextLesson = getNextLesson(lesson.id);

  const ensureStarted = useCallback(async () => {
    if (!started) {
      setStarted(true);
      markStarted(lesson.id);
      if (token) {
        try {
          await startLesson(token, lesson.id);
        } catch {
          // Lesson may already be started — that's fine
        }
      }
    }
  }, [started, token, lesson.id]);

  const handleNext = async () => {
    await ensureStarted();

    if (isLastStage && quizAnswered) {
      // Complete the lesson — persist locally first, then sync to server
      markCompleted(lesson.id, quizSelectedIndex, quizCorrect ? 1 : 0);
      if (token) {
        try {
          await completeLesson(token, lesson.id, quizSelectedIndex, quizCorrect ? 1 : 0);
        } catch { /* local progress saved */ }

        // Award XP
        try {
          let totalXpEarned = 50;
          const res = await addXp(token, 50, "lesson_complete");
          setXp({ totalXp: res.totalXp, weeklyXp: res.weeklyXp, currentStreak: res.currentStreak });

          if (quizCorrect) {
            const bonus = await addXp(token, 25, "perfect_quiz");
            totalXpEarned += 25;
            setXp({ totalXp: bonus.totalXp, weeklyXp: bonus.weeklyXp });
            if (bonus.newBadges.length > 0) {
              addBadges(bonus.newBadges);
              setNewBadges((b) => [...b, ...bonus.newBadges]);
            }
          }

          if (res.newBadges.length > 0) {
            addBadges(res.newBadges);
            setNewBadges((b) => [...b, ...res.newBadges]);
          }

          setEarnedXp(totalXpEarned);
        } catch { /* XP sync failed — not critical */ }

        // Seed spaced repetition reviews
        try {
          await seedReviews(token, lesson.id);
        } catch { /* review seeding failed — not critical */ }
      }
      trackEvent("lesson_complete", { lesson_id: lesson.id });
      setCompleted(true);
      return;
    }

    if (!isLastStage) {
      const nextStage = currentStage + 1;
      setDirection("forward");
      setStageKey((k) => k + 1);
      setCurrentStage(nextStage);
      markStage(lesson.id, nextStage);
      if (token) {
        try {
          await updateStage(token, lesson.id, nextStage);
        } catch {
          // Server sync failed — local progress is saved
        }
      }
    }
  };

  const handlePrev = () => {
    if (currentStage > 0) {
      setDirection("backward");
      setStageKey((k) => k + 1);
      setCurrentStage(currentStage - 1);
    }
  };

  const handleQuizAnswer = (selectedIndex: number, isCorrect: boolean) => {
    setQuizAnswered(true);
    setQuizCorrect(isCorrect);
    setQuizSelectedIndex(selectedIndex);
  };

  const handleNextLesson = () => {
    if (nextLesson) {
      navigate(`/learn/${nextLesson.id}`);
    }
  };

  const handleBackToAcademy = () => {
    navigate("/learn");
  };

  if (completed) {
    return (
      <LessonComplete
        lesson={lesson}
        quizCorrect={quizCorrect}
        nextLesson={nextLesson}
        earnedXp={earnedXp}
        newBadges={newBadges}
        onNextLesson={handleNextLesson}
        onBackToAcademy={handleBackToAcademy}
      />
    );
  }

  const canAdvance = isLastStage ? quizAnswered : true;

  return (
    <div className={cn("min-h-screen bg-background flex flex-col", `level-${lesson.moduleId}`)}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-white/[0.06]">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={handleBackToAcademy}
            aria-label="Return to academy"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 -ml-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold truncate">{lt(lesson.title, lang)}</h1>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5" aria-live="polite">
              {currentStage + 1} / {lesson.stages.length}
              <span className="text-xs" role="img" aria-label={stage.type}>
                {STAGE_TYPE_ICONS[stage.type]}
              </span>
            </p>
          </div>
        </div>
        <StageProgress
          currentStage={currentStage}
          totalStages={lesson.stages.length}
        />
      </div>

      {/* Content */}
      <div role="main">
      <div
        key={stageKey}
        className="flex-1 px-4 py-5 pb-40 max-w-screen-xl mx-auto w-full"
        style={{ animation: `${direction === "forward" ? "slideInRight" : "slideInLeft"} 0.3s ease-out` }}
      >
        {stage.type === "intro" && <StageIntro stage={stage} />}
        {stage.type === "theory" && <StageTheory stage={stage} />}
        {(stage.type === "simulation" ||
          stage.type === "simulation_adjust") && (
          <StageSimulation stage={stage} />
        )}
        {stage.type === "summary" && (
          <StageSummary stage={stage} onQuizAnswer={handleQuizAnswer} />
        )}
        {stage.type === "hook" && (
          <StageHook stage={stage} onAnswer={() => {}} />
        )}
        {stage.type === "concept" && <StageConcept stage={stage} />}
        {stage.type === "decision" && <StageDecision stage={stage} />}
        {stage.type === "reveal" && <StageReveal stage={stage} />}
        {stage.type === "takeaway" && <StageTakeaway stage={stage} />}
        {stage.type === "quiz" && (
          <StageQuiz
            stage={stage}
            onComplete={(correct, total) => {
              handleQuizAnswer(0, correct === total);
            }}
          />
        )}
      </div>
      </div>

      {/* Navigation buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t border-white/[0.06] px-4 py-4 pb-safe">
        <div className="flex gap-3 max-w-screen-xl mx-auto">
          {currentStage > 0 && (
            <Button variant="outline" className="flex-1" onClick={handlePrev}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("learn_prev")}
            </Button>
          )}
          <Button
            className="flex-1 bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            onClick={handleNext}
            disabled={!canAdvance}
          >
            {isLastStage
              ? t("learn_finish")
              : t("learn_next")}
            {!isLastStage && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
